import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState, assetTypeLabel } from "@/components/shared";
import { Plus, Layers, Pencil, Trash2 } from "lucide-react";

export default function AssetTypes() {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/asset-types").then(({ data }) => setTypes(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    try {
      if (editId) await api.put(`/asset-types/${editId}`, form);
      else await api.post("/asset-types", form);
      toast.success(editId ? "Tipologia aggiornata" : "Tipologia creata");
      setOpen(false); setForm({ name: "", description: "" }); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare la tipologia "${assetTypeLabel(t.name)}"?`)) return;
    try { await api.delete(`/asset-types/${t.id}`); toast.success("Tipologia eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Configurazione" title="Tipologie" subtitle="Definisci le tipologie dei beni (mezzo, attrezzatura, ...)">
        <Button onClick={() => { setForm({ name: "", description: "" }); setEditId(null); setOpen(true); }} data-testid="new-asset-type-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova tipologia
        </Button>
      </PageHeader>

      {types.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map((t) => (
            <Card key={t.id} data-testid={`asset-type-${t.id}`} className="p-5 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-primary" />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ name: t.name, description: t.description || "" }); setEditId(t.id); setOpen(true); }} data-testid={`edit-asset-type-${t.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-asset-type-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="font-heading font-bold text-lg mt-3 capitalize">{assetTypeLabel(t.name)}</div>
              {t.description && <p className="text-sm text-muted-foreground mt-1">{t.description}</p>}
              <div className="label-caps text-muted-foreground mt-3">{t.asset_count} beni</div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-lg"><EmptyState icon={Layers} title="Nessuna tipologia" subtitle="Crea la prima tipologia per classificare i beni." /></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Modifica tipologia" : "Nuova tipologia"}</DialogTitle>
            <DialogDescription>Le tipologie sono selezionabili quando crei o modifichi un bene.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="asset-type-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Mezzo, Attrezzatura, Dispositivo" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-asset-type-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
